/**
 * 客户档案表单 + 「每本书」的讲述人与背景表单。字段口径见草案 §9。
 *
 * 一位客户（付款的子女）可以给爸妈各开一单、各一本书，所以讲述人与背景属于**每本书（订单）**，
 * 客户档案只剩称呼、电话。
 *   - CustomerFields        客户称呼、联系电话（建档弹窗与详情页共用）
 *   - BookNarratorFields    讲述人姓名、与客户的关系（开单弹窗与书档案共用；关系必填）
 *   - BookBackgroundFields  背景：人生骨架、关键人物、禁区、交付……（书档案）
 *
 * 必填：客户称呼、讲述人与客户的关系；书档案保存时另需禁区（可填「无」）、期望交付日期、操作方式。
 * 必填只在表单层把关——背景信息是给人看的，后端只校验结构与大小。
 */
import { AutoComplete, Col, Divider, Form, Input, InputNumber, Row, Select } from 'antd';
import type {
  AdvisorBackground,
  AdvisorBookProfileInput,
  AdvisorCustomerDetailDto,
  AdvisorCustomerInput,
  AdvisorOrderDto,
} from '../../../api/advisor';

const RELATION_OPTIONS = ['母亲', '父亲', '外婆', '外公', '奶奶', '爷爷', '本人', '配偶'].map((v) => ({ value: v }));

const OPERATION_MODE_OPTIONS = [
  { value: 'elder_self', label: '老人自己用手机' },
  { value: 'child_assist', label: '子女代为操作' },
  { value: 'together', label: '一起操作' },
];

/** 客户（付款的子女）：只有称呼与电话 */
export interface CustomerFormValues {
  displayName: string;
  contactPhone?: string;
}

/** 一本书（订单）的讲述人与背景 */
export interface BookProfileFormValues {
  narratorName?: string;
  narratorRelation?: string;
  background?: AdvisorBackground;
}

const { TextArea } = Input;

export function CustomerFields() {
  return (
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Form.Item
          name="displayName"
          label="客户称呼"
          tooltip="只在后台显示，用户看不到"
          rules={[{ required: true, whitespace: true, message: '请填写客户称呼' }, { max: 64 }]}
        >
          <Input placeholder="如：李姐" />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="contactPhone" label="联系电话" rules={[{ max: 32 }]}>
          <Input placeholder="仅作联系用，不用于登录" />
        </Form.Item>
      </Col>
    </Row>
  );
}

/** 这本书写的是谁：姓名 + 与客户的关系（关系必填） */
export function BookNarratorFields() {
  return (
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Form.Item name="narratorName" label="讲述人" tooltip="这本书写的是谁" rules={[{ max: 64 }]}>
          <Input placeholder="如：王秀英" />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="narratorRelation"
          label="讲述人是客户的"
          rules={[{ required: true, whitespace: true, message: '请填写关系' }, { max: 32 }]}
        >
          <AutoComplete options={RELATION_OPTIONS} placeholder="如：母亲" />
        </Form.Item>
      </Col>
    </Row>
  );
}

/** 书档案：讲述人 + 背景全部字段 */
export function BookProfileFields() {
  return (
    <>
      <Divider orientation="left" plain>讲述人</Divider>
      <BookNarratorFields />
      <BookBackgroundFields />
    </>
  );
}

function BookBackgroundFields() {
  return (
    <>
      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Form.Item name={['background', 'gender']} label="性别">
            <Select allowClear options={[{ value: '女' }, { value: '男' }]} />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item name={['background', 'birthYear']} label="出生年份">
            <Input placeholder="如：1948" />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item name={['background', 'birthPlace']} label="出生地 / 籍贯">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item name={['background', 'residence']} label="现居地">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" plain>人生骨架</Divider>
      <Form.Item name={['background', 'lifeSkeleton']} label="经历" tooltip="求学、工作单位、参军 / 下乡 / 迁居、婚姻、子女、退休">
        <TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
      </Form.Item>
      <Form.Item name={['background', 'keyPeople']} label="关键人物" tooltip="写名字和关系，问题里会直接用名字问">
        <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="如：老伴 李建国；大女儿 李梅" />
      </Form.Item>
      <Form.Item name={['background', 'highlights']} label="高光与转折" tooltip="最骄傲的事、最难的时期、家里最常讲的故事">
        <TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
      </Form.Item>
      <Form.Item name={['background', 'eraMarks']} label="时代印记">
        <TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder="如：知青、恢复高考、下岗" />
      </Form.Item>

      <Divider orientation="left" plain>禁区</Divider>
      <Form.Item
        name={['background', 'taboos']}
        label="不想提的话题"
        tooltip="问题清单与叙叙对话都会避开。没有就填「无」"
        rules={[{ required: true, whitespace: true, message: '请填写禁区，没有就填「无」' }]}
      >
        <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="如：父亲去世的那段；离婚" />
      </Form.Item>

      <Divider orientation="left" plain>交付</Divider>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            name={['background', 'deadline']}
            label="期望交付日期"
            tooltip="寿宴、纪念日等，决定每天推几题"
            rules={[{ required: true, message: '请填写期望交付日期' }]}
          >
            <Input type="date" />
          </Form.Item>
        </Col>
        <Col xs={12} md={8}>
          <Form.Item name={['background', 'copies']} label="印刷本数">
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={12} md={8}>
          <Form.Item
            name={['background', 'operationMode']}
            label="谁来操作"
            rules={[{ required: true, message: '请选择操作方式' }]}
          >
            <Select options={OPERATION_MODE_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name={['background', 'writtenFor']} label="写给谁 / 想留下什么">
        <Input placeholder="如：写给孙子，让他知道我们家从哪儿来" />
      </Form.Item>
      <Form.Item name={['background', 'conditions']} label="老人的情况" tooltip="听力、方言、识字">
        <TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
      </Form.Item>
      <Form.Item name={['background', 'materials']} label="已有素材">
        <TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder="如：老照片约 50 张、两本日记" />
      </Form.Item>
      <Form.Item name={['background', 'stylePreference']} label="风格偏好">
        <TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder="书名想法、语气" />
      </Form.Item>
    </>
  );
}

/** 去掉空串与 undefined；背景一个字段都没填时返回 null */
function compactBackground(bg: AdvisorBackground | undefined): AdvisorBackground | null {
  if (!bg) return null;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(bg)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) out[k] = t;
    } else {
      out[k] = v;
    }
  }
  return Object.keys(out).length ? (out as AdvisorBackground) : null;
}

const blankToNull = (v: string | undefined) => (v && v.trim() ? v.trim() : null);

export function customerToPayload(values: CustomerFormValues): AdvisorCustomerInput {
  return {
    displayName: values.displayName.trim(),
    contactPhone: blankToNull(values.contactPhone),
  };
}

export function bookProfileToPayload(values: BookProfileFormValues): AdvisorBookProfileInput {
  return {
    narratorName: blankToNull(values.narratorName),
    narratorRelation: blankToNull(values.narratorRelation),
    background: compactBackground(values.background),
  };
}

export function customerToFormValues(d: AdvisorCustomerDetailDto): CustomerFormValues {
  return { displayName: d.displayName, contactPhone: d.contactPhone ?? undefined };
}

export function orderToBookFormValues(o: AdvisorOrderDto): BookProfileFormValues {
  return {
    narratorName: o.narratorName ?? undefined,
    narratorRelation: o.narratorRelation ?? undefined,
    background: o.background ?? undefined,
  };
}
